/**
 * Member Dashboard Page for Re:Nav-96
 *
 * The personalized dashboard view for authenticated members.
 * Shows event information, quick stats, and personalized content.
 *
 * Features:
 * - Welcome message with user's name
 * - Countdown to reunion date
 * - Quick action cards
 * - Photo carousel with auto-rotating gallery images
 * - Latest messages marquee with scrolling updates
 * - Recent activity summary (placeholder for future implementation)
 *
 * @example
 * // In router configuration
 * { path: 'dashboard', element: <DashboardPage /> }
 */

import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { useAuth } from '@/hooks/useAuth';
import { useMyMemberships } from '@/hooks/useCan';
import { chaptersApi, type ChapterSummary } from '@/services/api';
import { CountdownTimer } from '@/components/public/CountdownTimer';
import { MessageMarquee } from '@/components/member/MessageMarquee';
import { PhotoCarousel } from '@/components/member/PhotoCarousel';

/**
 * Props for DashboardCard component
 */
interface DashboardCardProps {
  title: string;
  description: string;
  icon: React.ReactNode;
  linkTo: string;
  linkText: string;
}

/**
 * Dashboard action card component
 */
function DashboardCard({ title, description, icon, linkTo, linkText }: DashboardCardProps) {
  return (
    <div className="card group hover:scale-[1.02] transition-all duration-300">
      <div
        className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
        style={{
          background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.2) 0%, rgba(233, 69, 96, 0.05) 100%)',
          border: '1px solid rgba(233, 69, 96, 0.2)',
        }}
      >
        {icon}
      </div>
      <h3 className="text-lg font-display font-semibold text-white mb-2">{title}</h3>
      <p className="text-white/60 text-sm mb-4 leading-relaxed">{description}</p>
      <Link
        to={linkTo}
        className="text-primary text-sm font-medium hover:text-primary-light transition-colors inline-flex items-center gap-2"
      >
        {linkText}
        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
        </svg>
      </Link>
    </div>
  );
}

/**
 * Welcome section with personalized greeting
 */
function useDashboardScope(): {
  chapterDisplayName: string | null;
  loading: boolean;
} {
  const { memberships, loading: membershipsLoading } = useMyMemberships();
  const [chapters, setChapters] = useState<ChapterSummary[]>([]);
  const isInstitutionAdmin = memberships.some(
    (m) => m.scopeType === 'institution' && m.role === 'admin',
  );

  useEffect(() => {
    if (memberships.length === 0) return;
    chaptersApi.list('navarasam').then(setChapters).catch(() => undefined);
  }, [memberships.length]);

  // Direct chapter membership wins. Otherwise, for institution admins we
  // synthesize "all chapters" so the heading reflects something real.
  const direct = memberships.find((m) => m.scopeType === 'chapter');
  const fallback = isInstitutionAdmin && chapters.length === 1 ? chapters[0] : null;
  const chapter = direct ? chapters.find((c) => c.id === direct.scopeId) ?? null : fallback;

  return {
    chapterDisplayName: chapter?.displayName ?? (isInstitutionAdmin ? 'All chapters' : null),
    loading: membershipsLoading,
  };
}

function WelcomeSection() {
  const { user } = useAuth();
  const firstName = user?.displayName?.split(' ')[0] || 'Member';
  const { chapterDisplayName } = useDashboardScope();

  return (
    <div className="relative overflow-hidden rounded-2xl p-8"
      style={{
        background: 'linear-gradient(135deg, rgba(233, 69, 96, 0.15) 0%, rgba(45, 45, 74, 0.9) 100%)',
        border: '1px solid rgba(233, 69, 96, 0.2)',
      }}
    >
      {/* Background decoration */}
      <div
        className="absolute top-0 right-0 w-64 h-64 opacity-10"
        style={{
          background: 'radial-gradient(circle, rgba(233, 69, 96, 0.8) 0%, transparent 70%)',
        }}
      />

      <div className="relative z-10">
        {chapterDisplayName && (
          <p className="text-xs uppercase tracking-wide text-white/40 mb-1">{chapterDisplayName}</p>
        )}
        <h2 className="text-2xl md:text-3xl font-display font-bold text-white mb-2">
          Welcome back, {firstName}!
        </h2>
        <p className="text-white/70 max-w-2xl">
          We're excited to have you here. The 30th year reunion is coming up soon.
          Stay connected with your batchmates and get ready for an amazing celebration!
        </p>
      </div>
    </div>
  );
}

/**
 * Countdown section with mini countdown display
 */
function CountdownSection() {
  return (
    <div className="card text-center">
      <h3 className="text-lg font-display font-semibold text-white mb-4">
        Countdown to Reunion
      </h3>
      <div className="scale-75 origin-center">
        <CountdownTimer />
      </div>
      <p className="text-white/50 text-sm mt-2">June 13th, 2026</p>
    </div>
  );
}

/**
 * Quick stats section showing member info
 */
function QuickStatsSection() {
  // These will be populated from API in future implementation
  const stats = [
    { label: 'Messages', value: '--', icon: '💬' },
    { label: 'Photos', value: '--', icon: '📷' },
    { label: 'Groups', value: '--', icon: '👥' },
  ];

  return (
    <div className="grid grid-cols-3 gap-4">
      {stats.map((stat) => (
        <div key={stat.label} className="card text-center py-4">
          <span className="text-2xl mb-2 block">{stat.icon}</span>
          <span className="text-2xl font-bold text-white">{stat.value}</span>
          <span className="text-white/50 text-xs block mt-1">{stat.label}</span>
        </div>
      ))}
    </div>
  );
}

/**
 * Quick actions section with navigation cards
 */
function QuickActionsSection() {
  const actions: DashboardCardProps[] = [
    {
      title: 'Photo Gallery',
      description: 'Browse and share photos from school days and past reunions.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
          />
        </svg>
      ),
      linkTo: '/member/photos',
      linkText: 'View Photos',
    },
    {
      title: 'Groups',
      description: 'Join discussions with your batchmates in various topic groups.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
          />
        </svg>
      ),
      linkTo: '/member/groups',
      linkText: 'Open Groups',
    },
    {
      title: 'Event Registration',
      description: 'Register for the reunion event and get your exclusive ticket.',
      icon: (
        <svg className="w-6 h-6 text-primary" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M15 5v2m0 4v2m0 4v2M5 5a2 2 0 00-2 2v3a2 2 0 110 4v3a2 2 0 002 2h14a2 2 0 002-2v-3a2 2 0 110-4V7a2 2 0 00-2-2H5z"
          />
        </svg>
      ),
      linkTo: '/register',
      linkText: 'Register Now',
    },
  ];

  return (
    <section>
      <h3 className="text-lg font-display font-semibold text-white mb-4">Quick Actions</h3>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {actions.map((action) => (
          <DashboardCard key={action.title} {...action} />
        ))}
      </div>
    </section>
  );
}

/**
 * Recent activity section (placeholder for future implementation)
 */
function RecentActivitySection() {
  return (
    <section className="card">
      <h3 className="text-lg font-display font-semibold text-white mb-4">Recent Activity</h3>
      <div className="text-center py-8">
        <svg
          className="w-12 h-12 text-white/20 mx-auto mb-4"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"
          />
        </svg>
        <p className="text-white/50 text-sm">
          Recent activity will appear here once you start engaging with the community.
        </p>
      </div>
    </section>
  );
}

/**
 * DashboardPage Component
 *
 * The main member dashboard displaying personalized content,
 * countdown timer, quick actions, and recent activity.
 *
 * @returns The member dashboard page content
 *
 * @example
 * // Used in router
 * { path: 'dashboard', element: <DashboardPage /> }
 */
export function DashboardPage() {
  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Welcome section */}
      <WelcomeSection />

      {/* Countdown and stats row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2">
          <CountdownSection />
        </div>
        <div>
          <QuickStatsSection />
        </div>
      </div>

      {/* Quick actions */}
      <QuickActionsSection />

      {/* Photo carousel and messages row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <PhotoCarousel />
        <MessageMarquee />
      </div>

      {/* Recent activity */}
      <RecentActivitySection />
    </div>
  );
}

export default DashboardPage;
